import { useState, useEffect, useRef, useMemo } from "react";
import { useCallback } from "react";

import { useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  Play,
  LogOut,
  MessageSquare,
  ChevronLeft,
  User,
  Volume2,
  Music,
  Check,
  XIcon,
  Megaphone,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { API_ENDPOINTS } from "../../lib/config";
import { fetchWithAuth } from "../../lib/authService";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "./BusinessSidebar";
import { motion } from "framer-motion";
import { EnhancedSongCard, EnhancedShoutCard } from "../../components/cards";
import NotificationBanner from "../../components/notification";
import {
  EndModal,
  UsersModal,
} from "../../components/ButtonModals";
import AnnouncementModal from "../../components/Announcement";
import PartnerGiftsModal from "../../components/PartnerGiftsModal";
import RejectTipModal from "../../components/RejectTipModal";

// Add CSS for shaking animation
const shakeKeyframes = `
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
}

.shake-animation {
  animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both infinite;
}
`;

// Add style tag to document head
const styleSheet = document.createElement("style");
styleSheet.innerText = shakeKeyframes;
document.head.appendChild(styleSheet);

export default function EventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  // State variables
  const [activeTab, setActiveTab] = useState("requests");
  const [venueDetails, setVenueDetails] = useState({});
  const [requests, setRequests] = useState([]);
  const [approved, setApproved] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [shoutRequests, setShoutRequests] = useState([]);
  const [shoutCount, setShoutCount] = useState(0);
  const [shoutHistory, setShoutHistory] = useState([]);
  const [chevshown, setChevshown] = useState(false);
  const [count, setCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("00:00:00");
  const [isPaused, setIsPaused] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [newApprovedItems, setNewApprovedItems] = useState({});
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [notificationType, setNotificationType] = useState(null); // 'song' or 'shoutout'
  const [shoutOut, setShoutOut] = useState(false);
  const [eventId, setEventId] = useState(
    localStorage.getItem("selectedEventId") || null,
  );
  const [tipsToday, setTipsToday] = useState(0);
  const [rejectTipModalData, setRejectTipModalData] = useState(null);
  const [isRejectingTip, setIsRejectingTip] = useState(false);
  const [showGiftsModal, setShowGiftsModal] = useState(false);

  // Tip Goal calculation matching Android VenueActivity.kt
  // First level is 50, and each next level is 2x of previous (50, 100, 200, 400, 800...)
  const { currentGoal, progressPercent } = useMemo(() => {
    let goal = 50;
    while (tipsToday >= goal && goal < 1000000000) {
      goal *= 2;
    }
    const percent = Math.min(
      100,
      Math.max(0, Math.round((tipsToday / goal) * 100))
    );
    return { currentGoal: goal, progressPercent: percent };
  }, [tipsToday]);

  const songNotificationSound = new Audio(
    "/sounds/mixkit-correct-answer-tone-2870.wav",
  );
  const shoutoutNotificationSound = new Audio(
    "/sounds/mixkit-software-interface-back-2575.wav",
  );
  // Sidebar state
  const [sidebarPage, setSidebarPage] = useState("home");
  const [profile, setProfile] = useState({});

  // Refs
  const prevApprovedRef = useRef([]);
  const prevRequestsRef = useRef([]);
  const countdownInterval = useRef(null);
  const sessionEndedRef = useRef(false);
  const lastPlayedRef = useRef(null);
  const lastActionTimeRef = useRef(Date.now()); // New ref to track last action time
  const pollingIntervalRef = useRef(null); // Ref to track polling interval


  // Format time for display
  const getValidTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return !isNaN(date.getTime()) ? date : null;
  };

  // Then use it in your formatTime:
  const formatTime = (dateString) => {
    const date = getValidTime(dateString);
    if (!date) return "--:--";
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Wrap formatTimer in useCallback
  const formatTimer = useCallback((timeLeft) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(timeLeft.hours)}:${pad(timeLeft.minutes)}:${pad(
      timeLeft.seconds,
    )}`;
  }, []);

  // Add this temporarily to check what times you're receiving
  useEffect(() => {
    if (requests.length > 0) {
      console.log(
        "Request times:",
        requests.map((r) => ({
          id: r.id,
          name: r.name,
          requestedAt: r.requestedAt,
          formattedTime: formatTime(r.requestedAt),
        })),
      );
    }
  }, [requests]);

  const calculateTimeRemaining = useCallback((endTime) => {
    const now = new Date().getTime();
    const end = new Date(endTime).getTime();
    const diff = end - now;

    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, total: 0 };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return {
      hours,
      minutes,
      seconds,
      total: diff,
    };
  }, []);

  const EndSession = useCallback(async () => {
    try {
      setSessionEnded(true);
      await fetchWithAuth(API_ENDPOINTS.EVENT_STATUS(eventId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "finished" }),
      });
      navigate("/loggedinartistbussiness");
    } catch (err) {
      console.error("Error ending session:", err);
    }
  }, [eventId, navigate]);

  const updateTimer = useCallback(() => {
    if (!venueDetails.endAt || isPaused) return;

    const timeLeft = calculateTimeRemaining(venueDetails.endAt);

    if (timeLeft.total <= 0) {
      clearInterval(countdownInterval.current);
      if (!sessionEndedRef.current) {
        sessionEndedRef.current = true;
        setTimeRemaining("00:00:00");
        EndSession();
      }
    } else {
      setTimeRemaining(formatTimer(timeLeft));
    }
  }, [
    venueDetails.endAt,
    isPaused,
    calculateTimeRemaining,
    formatTimer,
    EndSession,
  ]);

  useEffect(() => {
    if (!venueDetails.endAt) {
      setTimeRemaining("00:00:00");
      return;
    }

    if (isPaused) {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
      return;
    }

    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
    }

    updateTimer();

    countdownInterval.current = setInterval(updateTimer, 1000);

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [venueDetails.endAt, isPaused, updateTimer]);

  // Reset session ended ref when venue changes
  useEffect(() => {
    sessionEndedRef.current = false;
    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [venueDetails.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_QUEUE(id));
      if (!res.ok) throw new Error("Failed to fetch requests");
      const data = await res.json();
      console.log("requested after click", data);
      setRequests(data);
    } catch (err) {
      console.error("Error fetching requests:", err);
    }
  };

  const fetchApproved = async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_APPROVED_PARTNER(id));
      if (!res.ok) throw new Error("Failed to fetch approved");
      const data = await res.json();
      console.log("approved data after click", data);
      setApproved(data);
    } catch (err) {
      console.error("Error fetching approved:", err);
    }
  };

  // Function to fetch all data
  const fetchAllData = useCallback(async () => {
    try {
      const token = localStorage.getItem("tringAuthToken");
      if (!token) {
        console.error("Missing auth token");
        return;
      }

      const fetchVenueDetails = async () => {
        try {
          const venueRes = await fetchWithAuth(API_ENDPOINTS.VENUE_DETAILS(id));
          if (!venueRes.ok) throw new Error("Failed to fetch venue details");
          const venueData = await venueRes.json();
          setVenueDetails(venueData);
          console.log("venue data", venueData);
          // Do not override pause state from venue; use event status instead
          if (!("status" in venueData) || !venueData.status) {
            navigate("/loggedinartistbussiness");
          }
          setEventId(venueData.eventId);
          console.log("Fetched eventId:", venueData.eventId);
          updateTimer();
        } catch (err) {
          console.error("Error fetching venue details:", err);
        }
      };

      // Fetch current event state to determine pause/resume
      const fetchEventState = async () => {
        try {
          if (!eventId) return;
          const eventRes = await fetchWithAuth(
            API_ENDPOINTS.EVENT_DETAILS(eventId),
          );
          if (!eventRes.ok) throw new Error("Failed to fetch event details");
          const eventData = await eventRes.json();
          if (typeof eventData.status === "string") {
            setIsPaused(eventData.status !== "active");
          } else if (typeof eventData.isActive === "boolean") {
            setIsPaused(!eventData.isActive);
          }
        } catch (err) {
          console.error("Error fetching event details:", err);
        }
      };

      const fetchRequests = async () => {
        try {
          const res = await fetchWithAuth(API_ENDPOINTS.VENUE_QUEUE(id));
          if (!res.ok) throw new Error("Failed to fetch requests");
          const data = await res.json();
          console.log("request data", data);
          setRequests(data);
        } catch (err) {
          console.error("Error fetching requests:", err);
        }
      };

      const fetchApproved = async () => {
        try {
          const res = await fetchWithAuth(
            API_ENDPOINTS.VENUE_APPROVED_PARTNER(id),
          );
          if (!res.ok) throw new Error("Failed to fetch approved");
          const data = await res.json();
          setApproved(data);
        } catch (err) {
          console.error("Error fetching approved:", err);
        }
      };

      const fetchHistory = async () => {
        try {
          const res = await fetchWithAuth(
            API_ENDPOINTS.VENUE_PLAYBACK(id, 100),
          );
          if (!res.ok) throw new Error("Failed to fetch history");
          const data = await res.json();

          // Only update if there's an actual change in the data
          setHistoryData((prev) => {
            const prevIds = new Set(prev.map((p) => p.id));
            const newIds = new Set(data.map((d) => d.id));
            if (
              prevIds.size !== newIds.size ||
              !Array.from(prevIds).every((id) => newIds.has(id))
            ) {
              return data;
            }
            return prev;
          });
        } catch (err) {
          console.error("Error fetching history:", err);
        }
      };

      const fetchShoutRequests = async () => {
        try {
          const res = await fetchWithAuth(
            API_ENDPOINTS.SHOUT_OUT_USERS_REQUEST(id),
          );
          if (!res.ok) throw new Error("Failed to fetch shout requests");
          const data = await res.json();
          console.log("i am in fetchShoutRequests", data);

          setShoutRequests(data);
        } catch (err) {
          console.error("Error fetching shout requests:", err);
        }
      };

      const fetchShoutCount = async () => {
        try {
          const res = await fetchWithAuth(
            API_ENDPOINTS.SHOUT_OUT_USERS_COUNT(id),
          );
          if (!res.ok) throw new Error("Failed to fetch shout count");
          const { count } = await res.json();
          setShoutCount(count);
        } catch (err) {
          console.error("Error fetching shout count:", err);
        }
      };

      const fetchShoutHistory = async () => {
        try {
          const res = await fetchWithAuth(
            API_ENDPOINTS.SHOUT_OUT_USERS_HISTORY(id),
          );
          if (!res.ok) throw new Error("Failed to fetch shout history");
          const data = await res.json();
          console.log("shoutouthistory data here ", data);
          setShoutHistory(data);
        } catch (err) {
          console.error("Error fetching shout history:", err);
        }
      };

      const getcount = async () => {
        try {
          const res = await fetchWithAuth(API_ENDPOINTS.VENUE_USERS_COUNT(id));
          if (!res.ok) throw new Error("Failed to fetch count");
          const { count } = await res.json();
          setCount(count);
        } catch (err) {
          console.error("Error fetching count:", err);
        }
      };

      const getUsers = async () => {
        try {
          const res = await fetchWithAuth(API_ENDPOINTS.VENUE_USERS(id));
          if (!res.ok) throw new Error("Failed to fetch users");
          const data = await res.json();
          setUsers(data);
        } catch (err) {
          console.error("Error fetching users:", err);
        }
      };

      const fetchGiftEarnings = async () => {
        try {
          const currentEvtId = eventId || id;
          if (!currentEvtId) return;
          const res = await fetchWithAuth(
            API_ENDPOINTS.PARTNER_GIFTS_PAGINATED(0, 50, currentEvtId)
          );
          if (res.ok) {
            const body = await res.json();
            const totalInr = Number(
              body?.tipTotals?.totalInr ?? body?.totalEarnings ?? 0
            );
            setTipsToday(totalInr);
          }
        } catch (err) {
          console.error("Error fetching gift earnings for tip meter:", err);
        }
      };

      await fetchVenueDetails();
      await Promise.all([
        fetchEventState(),
        fetchHistory(),
        fetchApproved(),
        fetchRequests(),
        fetchShoutRequests(),
        fetchShoutCount(),
        fetchShoutHistory(),
        getcount(),
        getUsers(),
        fetchGiftEarnings(),
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [id, eventId, updateTimer]);

    // Trigger an immediate full refresh, then reset the timer so next auto-fetch waits full interval
    const triggerImmediateRefresh = useCallback(async () => {
      await fetchAllData();
      lastActionTimeRef.current = Date.now();
    }, [fetchAllData]);
  

  useEffect(() => {
    // Initial data fetch
    fetchAllData();

    // Set up polling with timer reset logic
    const pollData = () => {
      // Only fetch if at least 6 seconds have passed since the last action
      if (Date.now() - lastActionTimeRef.current >= 6000) {
        fetchAllData();
      }
    };

    pollingIntervalRef.current = setInterval(pollData, 6000);

    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [fetchAllData]);

  // Add this new useEffect
  useEffect(() => {
    if (lastPlayedRef.current) {
      const playedSongStillInApproved = approved.some(
        (a) => a.id === lastPlayedRef.current,
      );
      if (!playedSongStillInApproved) {
        lastPlayedRef.current = null;
      }
    }
  }, [approved]);

  // Track changes for animation highlighting
  useEffect(() => {
    // Check for new approved items
    const newItems = {};
    let hasNewItems = false;

    // Log current state for debugging
    console.log("Current approved items:", approved);
    console.log("Previous approved items:", prevApprovedRef.current);

    approved.forEach((item) => {
      if (!prevApprovedRef.current.some((prev) => prev.id === item.id)) {
        console.log("New approved item detected:", item.name);
        newItems[item.id] = true;
        hasNewItems = true;

        // Remove highlight after 2 seconds
        setTimeout(() => {
          setNewApprovedItems((prev) => {
            const updated = { ...prev };
            delete updated[item.id];
            return updated;
          });
        }, 2000);
      }
    });

    // Only set notification if we have new approved items
    if (hasNewItems) {
      console.log("Setting new items notification");
      setHasNewNotification(true);
      setTimeout(() => setHasNewNotification(false), 3000);
    }

    // Update approved items state and reference
    setNewApprovedItems((prev) => ({ ...prev, ...newItems }));
    prevApprovedRef.current = approved;

    // Cleanup function
    return () => {
      // Clear any pending timeouts if component unmounts
      Object.keys(newItems).forEach((id) => {
        clearTimeout();
      });
    };
  }, [approved]); // Only depend on approved items

  // Add this effect to watch for new requests

  useEffect(() => {
    console.log("Notification State:", { showNotification, notificationData });
  }, [showNotification, notificationData]);
  
  const isInitialMount = useRef(true);
  // Add this with your other refs
  const prevShoutRequestsRef = useRef([]);

  //For song requests
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevRequestsRef.current = requests;
      return;
    }

    if (requests.length <= prevRequestsRef.current.length) {
      prevRequestsRef.current = requests;
      return;
    }

    const newRequests = requests.filter(
      (r) => !prevRequestsRef.current.some((prev) => prev.id === r.id),
    );

    if (newRequests.length > 0) {
      const latestRequest = newRequests[0];
      setNotificationData({
        type: "song_request",
        title: latestRequest.name,
        artist: latestRequest.artists?.[0]?.name,
        userPhoto: latestRequest.users?.[0]?.avatar || "/placeholder.svg",
        image: latestRequest.album?.images?.[0]?.url || "/placeholder.svg",
      });
      setNotificationType("song");
      setShowNotification(true);
      songNotificationSound.play();

      setTimeout(() => setShowNotification(false), 5000);
    }

    prevRequestsRef.current = requests;
  }, [requests]);

  // For shoutout requests
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevShoutRequestsRef.current = shoutRequests;
      return;
    }

    if (shoutRequests.length <= prevShoutRequestsRef.current.length) {
      prevShoutRequestsRef.current = shoutRequests;
      return;
    }

    const newShoutouts = shoutRequests.filter(
      (s) => !prevShoutRequestsRef.current.some((prev) => prev._id === s._id),
    );

    if (newShoutouts.length > 0) {
      const latestShoutout = newShoutouts[0];
      setNotificationData({
        type: "shoutout",
        message: latestShoutout.shoutOut,
        additionalInfo: latestShoutout.additionalInfo,
        userPhoto: latestShoutout.user?.avatar || "/placeholder.svg",
        track: latestShoutout.track || null,
      });
      setNotificationType("shoutout");
      setShowNotification(true);
      shoutoutNotificationSound.play();

      setTimeout(() => setShowNotification(false), 5000);
    }

    prevShoutRequestsRef.current = shoutRequests;
  }, [shoutRequests]);

  const removeItemInstantly = (itemId, stateSetter) => {
    stateSetter((prev) => prev.filter((item) => item.id !== itemId));
  };

  const fetchRequestsDirect = useCallback(async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_QUEUE(id));
      if (!res.ok) throw new Error("Failed to fetch requests");
      const data = await res.json();
      setRequests(data);
    } catch (err) {
      console.error("Error fetching requests:", err);
    }
  }, [id]);

  const fetchApprovedDirect = useCallback(async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_APPROVED_PARTNER(id));
      if (!res.ok) throw new Error("Failed to fetch approved");
      const data = await res.json();
      setApproved(data);
    } catch (err) {
      console.error("Error fetching approved:", err);
    }
  }, [id]);

  const fetchShoutRequestsDirect = useCallback(async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.SHOUT_OUT_USERS_REQUEST(id));
      if (!res.ok) throw new Error("Failed to fetch shout requests");
      const data = await res.json();
      setShoutRequests(data);
    } catch (err) {
      console.error("Error fetching shout requests:", err);
    }
  }, [id]);

  // ---- Centralized updateTrackStatus ----
  const updateTrackStatus = async (trackId, status) => {
    try {
      const response = await fetchWithAuth(
        API_ENDPOINTS.VENUE_QUEUE_STATUS(id),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status,
            trackId,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error updating status:", errorText);
        toast.error("Failed to update track status");
        return false;
      }

      toast.success(`Track status updated to ${status}`);
      return true;
    } catch (err) {
      console.error("Error updating track status:", err);
      toast.error(`Failed to update track status: ${err.message}`);
      return false;
    }
  };

  // ---- Decline (Reject) ----
  const executeDecline = async (itemId) => {
    try {
      setIsRejectingTip(true);
      removeItemInstantly(itemId, setRequests);
      await updateTrackStatus(itemId, "rejected");
    } finally {
      setIsRejectingTip(false);
      setRejectTipModalData(null);
    }
  };

  // ---- Decline (Reject) ----
  const handleDecline = async (itemId) => {
    // Reset the timer when an action is taken
    lastActionTimeRef.current = Date.now();

    if (requests[0]?.id === itemId) {
      const item = requests[0];
      const tipVal =
        item?.djTipValueInr ||
        (item?.djTipCoins ? Number(item.djTipCoins) : 0) ||
        (item?.tipAmount ? Number(item.tipAmount) : 0) ||
        0;

      if (tipVal > 0) {
        setRejectTipModalData({
          itemId,
          track: item,
          tipAmount: tipVal,
        });
      } else {
        await executeDecline(itemId);
      }
    } else {
      toast.error(
        "Please decline the first request in the queue before others.",
      );
    }
  };

  // ---- Accept (Approve) ----
  const handleAccept = async (itemId) => {
    // Reset the timer when an action is taken
    lastActionTimeRef.current = Date.now();

    if (requests[0]?.id === itemId) {
      const item = requests.find((r) => r.id === itemId);

      // optimistic UI update
      setRequests((prev) => prev.filter((r) => r.id !== itemId));
      setApproved((prev) => [item, ...prev]);
      setNewApprovedItems((prev) => ({ ...prev, [itemId]: true }));

      const success = await updateTrackStatus(itemId, "approved");
      if (!success) {
        // rollback
        setRequests((prev) =>
          [...prev, item].sort(
            (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
          ),
        );
        setApproved((prev) => prev.filter((a) => a.id !== itemId));
      } 
      // remove highlight after 2s
      setTimeout(() => {
        setNewApprovedItems((prev) => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
      }, 2000);
    } else {
      toast.error(
        "Please accept the first request in the queue before others.",
      );
    }
  };

  // ---- Mark Played ----
  const handlePlayed = async (itemId) => {
    // Reset the timer when an action is taken
    lastActionTimeRef.current = Date.now();

    const item = approved.find((r) => r.id === itemId);
    lastPlayedRef.current = itemId;

    // optimistic update
    setApproved((prev) => prev.filter((a) => a.id !== itemId));
    item.status = "played";
    setHistoryData((prev) => [item, ...prev]);

    const success = await updateTrackStatus(itemId, "played");
    if (!success) {
      // rollback
      lastPlayedRef.current = null;
      setApproved((prev) =>
        [...prev, item].sort(
          (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
        ),
      );
      setHistoryData((prev) => prev.filter((h) => h.id !== itemId));
    } else {
      // Immediate full refresh, then wait full interval for next poll
      await triggerImmediateRefresh();
    }
  };

  const PauseSession = async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.EVENT_STATUS(eventId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "paused" }),
      });
      if (res.ok) {
        setIsPaused(true);

        updateTimer();

        toast.success("Session paused successfully");
      }
    } catch (err) {
      console.error("Error pausing session:", err);
    }
  };
  const ResumeSession = async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.EVENT_STATUS(eventId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "active" }),
      });
      if (res.ok) {
        setIsPaused(false); // <-- Set paused state
        toast.success("Session resumed successfully");
      }
    } catch (err) {
      console.error("Error resuming session:", err);
    }
  };


  // Toggle pause/resume
  const togglePause = async () => {
    try {
      const newStatus = isPaused ? "active" : "paused";

      await fetchWithAuth(API_ENDPOINTS.VENUE_STATUS(id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: newStatus,
        }),
      });

      setIsPaused(!isPaused);
      toast.success(`Session ${isPaused ? "resumed" : "paused"} successfully`);

      if (isPaused) {
        updateTimer();
      } else {
        if (countdownInterval.current) clearInterval(countdownInterval.current);
      }
    } catch (err) {
      console.error("Error toggling pause:", err);
      toast.error("Failed to toggle session status");
    }
  };

  // Fetch profile for sidebar avatar if needed
  useEffect(() => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }
    const fetchProfile = async () => {
      try {
        const profRes = await fetchWithAuth(API_ENDPOINTS.PARTNER_PROFILE);
        const prof = await profRes.json();
        setProfile(prof);
      } catch (err) {
        setProfile({});
      }
    };
    fetchProfile();
  }, []);

  const handleNotYet = () => {
    toast.success("No Problem we will wait");
  };

  const handleSidebarNav = (page) => {
    setSidebarPage(page);
    switch (page) {
      case "home":
        navigate("/loggedinartistbussiness");
        break;
      case "calendar":
        navigate("/businessCalendar");
        break;
      case "qr":
        navigate("/BusinessQr");
        break;
      case "rewards":
        navigate("/rewards");
        break;
      case "profile":
        navigate("/businessProfile");
        break;
      default:
        break;
    }
  };

  const isShoutTab =
    activeTab === "shout-requests" || activeTab === "shout-history";

  // Handle pause with confirmation
  const handlePause = () => {
    setShowPauseConfirm(true);
  };

  // Handle resume with confirmation
  const handleResume = () => {
    setShowResumeConfirm(true);
  };

  // Handle end with confirmation
  const handleEnd = () => {
    setShowEndConfirm(true);
  };
  // Memoize the requests and approved lists to prevent unnecessary re-renders
  const memoizedRequests = useMemo(() => requests, [requests]);
  const memoizedApproved = useMemo(() => approved, [approved]);

  // Memoize the EnhancedSongCard component

  const ShoutoutPlayed = async (sid) => {
    // Reset the timer when an action is taken
    lastActionTimeRef.current = Date.now();

    // 1. Find the shoutout
    const shout = shoutRequests.find((s) => s._id === sid);

    // 2. Optimistically update UI
    setShoutRequests((prev) => prev.filter((s) => s._id !== sid));
    setShoutHistory((prev) => [shout, ...prev]);

    try {
      const res = await fetchWithAuth(API_ENDPOINTS.SHOUT_OUT_STATUS(id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: "played",
          shoutOutId: sid,
        }),
      });

      if (!res.ok) {
        // 3. Revert UI if failed
        setShoutRequests((prev) => [shout, ...prev]);
        setShoutHistory((prev) => prev.filter((s) => s._id !== sid));
        toast.error("Failed to mark shoutout as played");
        return;
      }

      toast.success("Shoutout played successfully!");
    } catch (err) {
      // 3. Revert UI if error
      setShoutRequests((prev) => [shout, ...prev]);
      setShoutHistory((prev) => prev.filter((s) => s._id !== sid));
      console.error("Error fetching shoutout:", err);
      toast.error("Failed to mark shoutout as played");
    }
  };
  const [announce, setAnnounce] = useState(false);
  const [modalTab, setModalTab] = useState("requests");
  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 w-80 h-full z-40">
        <Sidebar
          currentPage={sidebarPage}
          onPageChange={handleSidebarNav}
          profile={profile}
        />
      </div>

      <AnnouncementModal
        isOpen={announce}
        onClose={() => {
          setAnnounce(false);
        }}
      />

      {/* Main Content */}
      <div className="flex-1 pl-0 lg:pl-80 w-full min-h-screen relative flex flex-col">
        {/* Sticky Header Section (Unified Purple Header + Tip-o-meter + Tabs) */}
        <div className="sticky top-0 z-40 w-full shadow-sm">
          {/* Top Purple Banner */}
          <div className="bg-gradient-to-r from-logo via-[#6720A3] to-[#6D2398] px-4 py-3.5 text-white relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {Array.from({ length: 20 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full bg-white/10"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    width: `${Math.random() * 40 + 10}px`,
                    height: `${Math.random() * 40 + 10}px`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.3, 0.8, 0.3],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 3 + Math.random() * 2,
                    repeat: Number.POSITIVE_INFINITY,
                    delay: Math.random() * 5,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </div>

            <div className="relative z-10 flex items-center justify-between ">
              <div className="flex items-center space-x-6 w-full">
                <div className="w-full">
                  <motion.h1
                    className="text-2xl lg:text-3xl font-bold flex items-center space-x-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    {isShoutTab ? (
                      <button
                        onClick={() => {
                          setActiveTab("requests");
                          setChevshown(false);
                        }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors mr-2"
                      >
                        <ChevronLeft className="h-5 w-5 lg:h-6 lg:w-6" />
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate("/loggedinartistbussiness")}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors mr-2"
                      >
                        <ChevronLeft className="h-5 w-5 lg:h-6 lg:w-6" />
                      </button>
                    )}
                    <span className="truncate">{venueDetails.name}</span>
                  </motion.h1>
                  <div className="w-full flex">
                    <motion.div
                      className="mt-3 flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-2 sm:space-y-0"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 lg:h-5 lg:w-5" />
                        <span className="text-xs lg:text-sm">Ends In:</span>
                        <span className="font-mono text-lg lg:text-xl font-bold">
                          {timeRemaining}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Volume2 className="h-4 w-4 lg:h-5 lg:w-5" />
                        <span className="text-xs lg:text-sm">Live Session</span>
                      </div>
                    </motion.div>

                    <div className="flex-1" />

                    <div className="flex md:gap-3 gap-1 mt-3 md:flex-row flex-row items-start md:items-end lg:relative absolute bottom-0 right-0">
                      {(localStorage.getItem("male_announcement") ||
                        localStorage.getItem("female_announcement")) && (
                        <motion.div
                          className="flex items-center space-x-4"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.6, delay: 0.3 }}
                        >
                          <button
                            onClick={() => setAnnounce(true)}
                            className="flex items-center justify-center space-x-2 rounded-xl bg-white/20 px-2 lg:px-4 py-1 lg:py-3 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-colors"
                          >
                            <Megaphone className="h-4 w-4 lg:h-5 lg:w-5" />
                            <span className="gap-1 text-xs lg:text-sm">
                              Announce
                            </span>
                          </button>
                        </motion.div>
                      )}
                      <motion.div
                        className="flex items-center space-x-4"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                      >
                        <button
                          onClick={() => setShowUsersModal(true)}
                          className="flex items-center justify-center space-x-2 rounded-xl bg-white/20 px-2 lg:px-4 py-1 lg:py-3 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-colors"
                        >
                          <User className="h-4 w-4 lg:h-5 lg:w-5" />
                          <span className="gap-1 text-xs lg:text-sm">
                            {count} Live
                          </span>
                        </button>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tipo Meter Card (Matching Android VenueActivity TIPS TODAY / आज की कमाई) */}
            <div
              onClick={() => setShowGiftsModal(true)}
              className="mt-3.5 w-full bg-black/35 hover:bg-black/45 backdrop-blur-md border border-white/25 rounded-2xl p-2.5 sm:p-3 shadow-md transition-all cursor-pointer group"
              title="Click to view Gifts & Tips breakdown"
            >
              <div className="flex items-center gap-3">
                {/* Gold ₹ Circle Badge */}
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 flex items-center justify-center text-[#4A0D5F] font-black text-sm sm:text-base shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform">
                  ₹
                </div>

                {/* Progress & Content Column */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] sm:text-xs font-bold text-white/90 tracking-wider">
                        TIPS TODAY
                      </span>
                      <span className="text-[10px] sm:text-xs font-medium text-white/60">
                        आज की कमाई
                      </span>
                    </div>
                    <span className="text-[10px] sm:text-xs font-bold text-amber-300">
                      Goal: ₹{currentGoal.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-sm sm:text-base font-extrabold text-amber-300 whitespace-nowrap">
                      ₹{tipsToday.toLocaleString()}
                    </span>
                    <div className="flex-1 h-2 sm:h-2.5 rounded-full bg-white/20 overflow-hidden relative shadow-inner">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-300 transition-all duration-500 shadow-xs"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <span className="text-[10px] sm:text-xs font-bold text-white/80 whitespace-nowrap">
                      {progressPercent}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Shoutout Button */}
            <button
              onClick={() => {
                setShoutOut(true);
                setChevshown(true);
              }}
              className={`
              mt-3 w-full p-2.5 sm:p-3 rounded-xl flex items-center justify-between transition-all
              ${
                activeTab === "shout-requests" || activeTab === "shout-history"
                  ? "bg-[#2c2c2c] shadow-lg"
                  : shoutRequests.length > 0
                    ? "bg-pink-600/60 shake-animation"
                    : "bg-[#2c2c2c] shadow-md"
              }
              transform hover:scale-[1.01] duration-200
            `}
            >
              <div className="flex items-center">
                <MessageSquare className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                <span className="text-sm lg:text-base font-bold">
                  SHOUT-OUTS ({shoutRequests.length})
                </span>
              </div>
              <div
                className={`
                rounded-full px-3 py-1 text-xs font-bold
                ${
                  shoutRequests.length > 0
                    ? "bg-red-400 text-white"
                    : "bg-white/20"
                }
              `}
              >
                {shoutRequests.length > 0 ? "NEW" : "NONE"}
              </div>
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200 bg-white">
            <div className="flex space-x-4 lg:space-x-8 px-4 overflow-x-auto">
              <>
                <motion.button
                  onClick={() => {
                    setActiveTab("requests");
                    fetchRequests();
                  }}
                  className={`relative flex items-center space-x-2 lg:space-x-3 py-3.5 text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    activeTab === "requests"
                      ? "text-logo"
                      : "text-[#4A4A4A] hover:text-logo"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Music className="h-4 w-4" />
                  <span>Requests</span>
                  {memoizedRequests.length > 0 && (
                    <motion.span
                      className="flex h-5 w-5 lg:h-6 lg:w-6 items-center justify-center rounded-full bg-red-500 text-xs text-white font-bold"
                      initial={{ scale: 0 }}
                      animate={{
                        scale: 1,
                        x: [0, -4, 4, -4, 4, 0],
                        transition: {
                          x: {
                            repeat: Infinity,
                            repeatType: "loop",
                            duration: 0.6,
                            ease: "easeInOut",
                          },
                          scale: { duration: 0.3 },
                        },
                      }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {memoizedRequests.length}
                    </motion.span>
                  )}
                  {activeTab === "requests" && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-logo to-[#6720A3] rounded-t-full"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                </motion.button>

                <motion.button
                  onClick={() => {
                    setActiveTab("accepted");
                    fetchApprovedDirect();
                  }}
                  className={`relative flex items-center space-x-2 lg:space-x-3 py-3.5 text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    activeTab === "accepted"
                      ? "text-logo"
                      : "text-[#4A4A4A] hover:text-logo"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Check className="h-4 w-4" />
                  <span>Accepted</span>
                  {memoizedApproved.length > 0 && (
                    <motion.span
                      className="flex h-5 w-5 lg:h-6 lg:w-6 items-center justify-center rounded-full bg-red-500 text-xs text-white font-bold"
                      initial={{ scale: 0 }}
                      animate={{
                        scale: 1,
                        x: [0, -4, 4, -4, 4, 0],
                        transition: {
                          x: {
                            repeat: Infinity,
                            repeatType: "loop",
                            duration: 0.6,
                            ease: "easeInOut",
                          },
                          scale: { duration: 0.3 },
                        },
                      }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {memoizedApproved.length}
                    </motion.span>
                  )}
                  {activeTab === "accepted" && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-logo to-[#6720A3] rounded-t-full"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                </motion.button>


                <motion.button
                  onClick={() => setActiveTab("history")}
                  className={`relative flex items-center space-x-2 lg:space-x-3 py-3.5 text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    activeTab === "history"
                      ? "text-logo"
                      : "text-[#4A4A4A] hover:text-logo"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Clock className="h-4 w-4" />
                  <span>History</span>
                  {activeTab === "history" && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-logo to-[#6720A3] rounded-t-full"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                </motion.button>
              </>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="md:p-4 p-2.5 flex-1 bg-gray-200">
          <div className="w-full mx-auto">
            <div className="space-y-4 lg:space-y-6">
              {activeTab === "requests" &&
                (memoizedRequests.length > 0 ? (
                  <div className="space-y-4 lg:space-y-6">
                    {memoizedRequests.map((item, index) => (
                      <EnhancedSongCard
                        key={item.id}
                        item={item}
                        index={index}
                        type="request"
                        isFirst={index === 0}
                        newApprovedItems={newApprovedItems}
                        handleDecline={handleDecline}
                        handleAccept={handleAccept}
                        handlePlayed={handlePlayed}
                        handleNotYet={handleNotYet}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 lg:py-20">
                    <div className="w-24 h-24 lg:w-32 lg:h-32 mx-auto mb-6 text-gray-300">
                      <svg
                        className="w-full h-full"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                        />
                      </svg>
                    </div>
                    <p className="text-xl lg:text-2xl font-medium text-gray-600 mb-2">
                      No requests at the moment
                    </p>
                    <p className="text-gray-500">
                      When fans send song requests, they'll appear here!
                    </p>
                  </div>
                ))}

              {activeTab === "accepted" &&
                (memoizedApproved.length > 0 ? (
                  <div className="space-y-4 lg:space-y-6">
                    {memoizedApproved.map((item, index) => (
                      <EnhancedSongCard
                        key={item.id}
                        item={item}
                        index={index}
                        type="approved"
                        isFirst={index === 0}
                        newApprovedItems={newApprovedItems}
                        handleDecline={handleDecline}
                        handleAccept={handleAccept}
                        handlePlayed={handlePlayed}
                        handleNotYet={handleNotYet}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 lg:py-20">
                    <div className="text-6xl lg:text-8xl mb-6">🎶</div>
                    <p className="text-xl lg:text-2xl font-medium text-gray-600 mb-2">
                      No approved songs in queue
                    </p>
                    <p className="text-gray-500">
                      Accept song requests to build your playlist!
                    </p>
                  </div>
                ))}


              {activeTab === "history" &&
                (historyData.length > 0 ? (
                  <div className="space-y-4 lg:space-y-6">
                    {historyData.map((item, index) => (
                      <EnhancedSongCard
                        key={item.id}
                        item={item}
                        index={index}
                        type="approved"
                        isFirst={index === 0}
                        newApprovedItems={newApprovedItems}
                        handleDecline={handleDecline}
                        handleAccept={handleAccept}
                        handlePlayed={handlePlayed}
                        handleNotYet={handleNotYet}
                        isHistory={true}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 lg:py-20">
                    <div className="text-6xl lg:text-8xl mb-6">📀</div>
                    <p className="text-xl lg:text-2xl font-medium text-gray-600 mb-2">
                      No playback history available
                    </p>
                    <p className="text-gray-500">
                      Songs you've played will appear here for reference
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </main>

        {/* Fixed Bottom Controls */}
        <div className="sticky bottom-0 left-0 z-20">
          <div className="w-full bg-gray-200 p-2 lg:p-4 border-t border-white/10">
            <div className="flex justify-center gap-1 lg:gap-4 max-w-4xl mx-auto">
              <button
                className="flex-1 lg:flex-initial lg:min-w-[140px] px-4 py-2 lg:px-6 lg:py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center font-medium transition-all duration-200 shadow-lg text-sm lg:text-base hover:scale-105"
                onClick={handleEnd}
              >
                <LogOut className="w-4 h-4 lg:w-5 lg:w-5 mr-2" />
                <span>End</span>
              </button>
            </div>
          </div>
        </div>

        {shoutOut && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 md:p-4 p-2">
            <motion.div
              className="bg-white rounded-2xl xl:w-1/2 lg:w-2/3 md:w-[90%] w-full shadow-2xl md:h-[80vh] h-[70vh] overflow-scroll"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              {/* Modal Header */}
              <div className="bg-white z-40 sticky top-0 w-full flex items-center justify-between pt-3 pb-1 px-3 border-b border-gray-100">
                <div className="w-full text-center">
                  <p className="text-2xl font-bold text-gray-900 leading-tight">
                    Shout-Outs
                  </p>
                  <p className="text-xs md:text-sm text-logo font-semibold mt-0.5 flex items-center justify-center gap-1.5">
                    <Volume2 className="w-3.5 h-3.5" /> Announce Shoutout On Mic
                  </p>
                </div>
                <button
                  onClick={() => setShoutOut(false)}
                  className="absolute right-4 top-3 text-gray-500 hover:text-gray-800 transition-colors"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="bg-white sticky top-[55px] z-30 flex justify-center gap-4 pb-2">
                <button
                  className={`px-4 py-2 rounded-full font-medium ${
                    modalTab === "requests"
                      ? "bg-logo text-white"
                      : "bg-white text-gray-700 border"
                  }`}
                  onClick={() => setModalTab("requests")}
                >
                  Requests
                </button>
                <button
                  className={`px-4 py-2 rounded-full font-medium ${
                    modalTab === "shout-history"
                      ? "bg-logo text-white"
                      : "bg-white text-gray-700 border"
                  }`}
                  onClick={() => setModalTab("shout-history")}
                >
                  History
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-3">
                {modalTab === "requests" ? (
                  shoutRequests.length > 0 ? (
                    <div className="space-y-2 lg:space-y-4">
                      {shoutRequests.map((shout, index) => (
                        <EnhancedShoutCard
                          key={shout._id}
                          shout={shout}
                          index={index}
                          type="request"
                          handleNotYet={handleNotYet}
                          ShoutoutPlayed={ShoutoutPlayed}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 lg:py-20">
                      <div className="text-6xl lg:text-8xl mb-6">📢</div>
                      <p className="text-xl lg:text-2xl font-medium text-gray-600 mb-4">
                        No shout-out requests available
                      </p>
                      <p className="text-gray-500">
                        When fans send shout-outs, they'll appear here!
                      </p>
                    </div>
                  )
                ) : shoutHistory.length > 0 ? (
                  <div className="space-y-4 lg:space-y-6">
                    {shoutHistory.map((shout, index) => (
                      <EnhancedShoutCard
                        key={shout._id}
                        shout={shout}
                        index={index}
                        type="history"
                        handleNotYet={handleNotYet}
                        ShoutoutPlayed={ShoutoutPlayed}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 lg:py-20">
                    <div className="text-6xl lg:text-8xl mb-6">📜</div>
                    <p className="text-xl lg:text-2xl font-medium text-gray-600 mb-2">
                      No shout-out history available
                    </p>
                    <p className="text-gray-500">
                      Completed shout-outs will be archived here
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}

        <EndModal
          open={showEndConfirm}
          onCancel={() => setShowEndConfirm(false)}
          onConfirm={() => {
            EndSession();
            setShowEndConfirm(false);
          }}
        />

        <UsersModal
          open={showUsersModal}
          onClose={() => setShowUsersModal(false)}
          users={users}
        />
        {/* Partner Gifts Breakdown Modal */}
        <PartnerGiftsModal
          isOpen={showGiftsModal}
          onClose={() => setShowGiftsModal(false)}
          eventId={eventId || id}
        />
        {/* Reject Tip Confirmation Modal */}
        <RejectTipModal
          isOpen={!!rejectTipModalData}
          onClose={() => setRejectTipModalData(null)}
          onConfirmDecline={() => {
            if (rejectTipModalData?.itemId) {
              executeDecline(rejectTipModalData.itemId);
            }
          }}
          tipAmount={rejectTipModalData?.tipAmount}
          track={rejectTipModalData?.track}
          isLoading={isRejectingTip}
        />
        {/* Toast Container */}
        <ToastContainer position="top-center" autoClose={3000} />
        {/* Notification Banner */}
        <NotificationBanner
          showNotification={showNotification}
          notificationData={notificationData}
          notificationType={notificationType}
          setShowNotification={setShowNotification}
        />
      </div>
    </div>
  );
}
